/**
 * Alert Workbook Page
 * 
 * Chat interface for creating a new alert with the alert-creation agent.
 * Maintains conversation context via threadId.
 * 
 * Opens with a naming dialog overlay (styled like AI Spotlight) that
 * blurs the background until user provides a workbook name.
 */

import AppLayout from '@layouts/AppLayout';
import { ActionIcon, Button, Group, Modal, Paper, ScrollArea, Stack, Text, Textarea } from '@mantine/core';
import { ArrowLeft, PaperPlaneTilt, Sparkle, X } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';

import { type AlertCreationResponse, deleteAlertThread, sendAlertCreationMessage } from './api';
import WorkbookNameDialog from './components/WorkbookNameDialog';

const userBubbleStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.07) 100%)',
    color: '#3730a3',
    border: '1px solid rgba(99, 102, 241, 0.18)',
    borderRadius: '16px 16px 4px 16px',
    padding: '9px 14px',
    maxWidth: 'min(75%, 520px)',
    marginLeft: 'auto',
    wordBreak: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
    fontSize: '13.5px',
    lineHeight: 1.55,
};

const assistantCardStyle: React.CSSProperties = {
    borderLeft: '2px solid rgba(99, 102, 241, 0.3)',
    paddingLeft: 14,
    paddingTop: 1,
    paddingBottom: 1,
    maxWidth: 'min(90%, 680px)',
};

const WaveDots = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {[0, 1, 2].map((i) => (
            <motion.span
                key={i}
                animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
                style={{
                    display: 'inline-block',
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: '#6366f1',
                }}
            />
        ))}
    </div>
);

const AgentAnchor = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
        <Sparkle size={10} weight="fill" color="#6366f1" />
        <span style={{
            fontSize: 10,
            fontWeight: 700,
            color: '#6366f1',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
        }}>
            Reparo AI
        </span>
    </div>
);

const markdownComponents = {
    p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
        <p style={{ margin: '0 0 8px', lineHeight: 1.6 }} {...props} />
    ),
    ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
        <ul style={{ paddingLeft: 18, margin: '4px 0 8px' }} {...props} />
    ),
    ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
        <ol style={{ paddingLeft: 18, margin: '4px 0 8px' }} {...props} />
    ),
    li: (props: React.LiHTMLAttributes<HTMLLIElement>) => (
        <li style={{ marginBottom: 3, lineHeight: 1.55 }} {...props} />
    ),
    strong: (props: React.HTMLAttributes<HTMLElement>) => (
        <strong style={{ fontWeight: 600, color: '#111827' }} {...props} />
    ),
    code: (props: React.HTMLAttributes<HTMLElement>) => (
        <code style={{ background: 'rgba(99,102,241,0.07)', color: '#6366f1', borderRadius: 4, padding: '1px 5px', fontFamily: 'ui-monospace, monospace', fontSize: 12 }} {...props} />
    ),
    pre: (props: React.HTMLAttributes<HTMLPreElement>) => (
        <pre style={{ background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: '12px 14px', overflowX: 'auto', fontSize: 12, margin: '8px 0', lineHeight: 1.6, fontFamily: 'ui-monospace, monospace' }} {...props} />
    ),
};

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
}

const AlertWorkbook: FC = () => {
    const navigate = useNavigate();

    // Workbook naming state - dialog shown on page load
    const [showNameDialog, setShowNameDialog] = useState<boolean>(true);
    const [alertLabel, setAlertLabel] = useState<string>('');

    // Chat state
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [threadId, setThreadId] = useState<string | undefined>(undefined);
    const [alertCreated, setAlertCreated] = useState<boolean>(false);

    // Exit confirmation
    const [exitModalOpen, setExitModalOpen] = useState<boolean>(false);

    // Scroll ref
    const scrollAreaRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (scrollAreaRef.current) {
            scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
    }, [messages]);

    // Generate unique message ID
    const generateMessageId = (): string => {
        return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    };

    // Handle sending a message
    const handleSendMessage = useCallback(async () => {
        if (!inputValue.trim() || isLoading) return;

        const userMessage: ChatMessage = {
            id: generateMessageId(),
            role: 'user',
            content: inputValue.trim(),
            timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);

        try {
            const response: AlertCreationResponse = await sendAlertCreationMessage({
                query: userMessage.content,
                alertLabel,
                threadId,
                contextLimit: 10,
            });

            // Store threadId for subsequent calls
            if (response.threadId && !threadId) {
                setThreadId(response.threadId);
            }

            // Add assistant response
            const assistantMessage: ChatMessage = {
                id: generateMessageId(),
                role: 'assistant',
                content: response.message,
                timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMessage]);

            // Check if alert was created
            if (response.alertCreated) {
                setAlertCreated(true);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage: ChatMessage = {
                id: generateMessageId(),
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    }, [inputValue, isLoading, alertLabel, threadId]);

    // Handle key press (Enter to send)
    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Handle exit
    const handleExit = () => {
        setExitModalOpen(true);
    };

    // Confirm exit
    const confirmExit = async () => {
        // Clean up thread if exists
        if (threadId) {
            await deleteAlertThread(threadId);
        }
        navigate('/alerts');
    };

    // Handle alert creation success
    const handleAlertCreatedContinue = () => {
        navigate('/alerts');
    };

    // Handle workbook name confirmation from dialog
    const handleWorkbookNameConfirm = (name: string) => {
        setAlertLabel(name);
        setShowNameDialog(false);
    };

    // Handle cancel from workbook name dialog
    const handleWorkbookNameCancel = () => {
        navigate('/alerts');
    };

    return (
        <AppLayout>
            {/* Workbook Name Dialog - shown on page load */}
            {showNameDialog && (
                <WorkbookNameDialog
                    onConfirm={handleWorkbookNameConfirm}
                    onCancel={handleWorkbookNameCancel}
                />
            )}

            <div 
                className="flex h-full w-full flex-col"
                style={{
                    filter: showNameDialog ? 'blur(4px)' : 'none',
                    pointerEvents: showNameDialog ? 'none' : 'auto',
                    transition: 'filter 200ms ease',
                }}
            >
                {/* Header */}
                <div className="border-b border-gray-200 bg-white px-6 py-4">
                    <Group justify="space-between" align="center">
                        <Group gap="md">
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="lg"
                                onClick={handleExit}
                                radius="md"
                            >
                                <ArrowLeft size={20} />
                            </ActionIcon>
                            <div>
                                <Text fw={600} size="lg" className="text-slate-800">
                                    {alertLabel || 'New Alert'}
                                </Text>
                            </div>
                        </Group>
                        <Button
                            variant="subtle"
                            color="gray"
                            leftSection={<X size={16} />}
                            onClick={handleExit}
                            radius="md"
                            size="sm"
                        >
                            Exit
                        </Button>
                    </Group>
                </div>

                {/* Chat Area */}
                <ScrollArea
                    className="flex-1 bg-gray-50"
                    viewportRef={scrollAreaRef}
                    p="md"
                >
                    <Stack gap="md" className="mx-auto max-w-2xl py-4">
                        {messages.length === 0 ? (
                            <div className="py-20 text-center">
                                <Text size="lg" fw={500} className="text-slate-600 mb-2">
                                    When should I alert you?
                                </Text>
                                <Text size="sm" c="dimmed" className="max-w-md mx-auto">
                                    Describe the condition that should trigger this alert.
                                    For example: "Alert me when stock falls below 30 days"
                                </Text>
                            </div>
                        ) : (
                            messages.map((message, i) => {
                                const prev = messages[i - 1];
                                const showAnchor =
                                    message.role === 'assistant' &&
                                    (!prev || prev.role === 'user');
                                return (
                                    <div key={message.id} style={{ display: 'flex', justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                        {message.role === 'user' ? (
                                            <div style={userBubbleStyle}>
                                                {message.content}
                                            </div>
                                        ) : (
                                            <div style={assistantCardStyle}>
                                                {showAnchor && <AgentAnchor />}
                                                <div style={{ fontSize: 13.5, lineHeight: 1.6, color: '#1a202c', wordBreak: 'break-word' }}>
                                                    <ReactMarkdown
                                                        components={markdownComponents}
                                                        remarkPlugins={[remarkGfm]}
                                                    >
                                                        {message.content}
                                                    </ReactMarkdown>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}

                        {isLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingLeft: 16, paddingTop: 4 }}>
                                <AgentAnchor />
                                <WaveDots />
                            </div>
                        )}

                        {alertCreated && (
                            <Paper
                                p="lg"
                                radius="md"
                                shadow="sm"
                                className="mx-auto mt-4 max-w-md bg-green-50 border border-green-200"
                            >
                                <Stack align="center" gap="md">
                                    <Text fw={500} className="text-green-700">
                                        🎉 Alert Created Successfully!
                                    </Text>
                                    <Text size="sm" c="dimmed" className="text-center">
                                        Your alert has been configured and will start monitoring.
                                    </Text>
                                    <Button
                                        onClick={handleAlertCreatedContinue}
                                        variant="filled"
                                        color="dark"
                                        radius="md"
                                        size="sm"
                                    >
                                        View All Alerts
                                    </Button>
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </ScrollArea>

                {/* Input Area */}
                {!alertCreated && (
                    <div className="border-t border-gray-200 bg-white p-4">
                        <div className="mx-auto max-w-2xl">
                            <Group gap="sm" align="flex-end">
                                <Textarea
                                    placeholder="Describe when you want to be alerted..."
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={isLoading}
                                    radius="md"
                                    minRows={1}
                                    maxRows={4}
                                    autosize
                                    className="flex-1"
                                    styles={{
                                        input: {
                                            fontSize: '14px',
                                        },
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleSendMessage}
                                    disabled={!inputValue.trim() || isLoading}
                                    style={
                                        inputValue.trim() && !isLoading
                                            ? {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 38,
                                                height: 38,
                                                borderRadius: 8,
                                                border: 'none',
                                                background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                                                boxShadow: '0 2px 6px rgba(99, 102, 241, 0.35)',
                                                color: 'white',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                            }
                                            : {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 38,
                                                height: 38,
                                                borderRadius: 8,
                                                border: 'none',
                                                background: 'rgba(0,0,0,0.07)',
                                                color: '#d1d5db',
                                                cursor: 'not-allowed',
                                                flexShrink: 0,
                                            }
                                    }
                                >
                                    <PaperPlaneTilt size={18} weight="fill" />
                                </button>
                            </Group>
                        </div>
                    </div>
                )}
            </div>

            {/* Exit Confirmation Modal */}
            <Modal
                opened={exitModalOpen}
                onClose={() => setExitModalOpen(false)}
                title={
                    <Text fw={600} size="md">
                        Exit Alert Creation?
                    </Text>
                }
                centered
                size="sm"
                radius="md"
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Are you sure you want to exit? Your conversation progress will be lost.
                    </Text>
                    <Group justify="flex-end">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={() => setExitModalOpen(false)}
                            radius="md"
                            size="sm"
                        >
                            Continue Creating
                        </Button>
                        <Button
                            variant="filled"
                            color="red"
                            onClick={confirmExit}
                            radius="md"
                            size="sm"
                        >
                            Exit
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </AppLayout>
    );
};

export default AlertWorkbook;
